import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Compass, 
  ArrowRight, 
  Utensils, 
  Camera, 
  Sparkles, 
  MapPin 
} from "lucide-react";

const curatedDestinations = [
  {
    id: "paris",
    title: "Parigi",
    tagline: "Bistrot d'autore & musei intimi",
    style: "Romantico & Gourmet",
    badge: "Romantico",
    // Foto diurna, cielo limpido, Torre Eiffel luminosa e solare
    image: "https://images.unsplash.com/photo-1511739001486-6bfe10ce785f?auto=format&fit=crop&w=1200&q=85",
    highlights: [
      { title: "Atmosfere Iconiche", text: "Passeggiate nel Marais storico e cene a lume di candela a Saint-Germain" },
      { title: "Arte & Scorci", text: "Capolavori al Musée d'Orsay e la luce dorata del tramonto lungo la Senna" },
      { title: "Segreti Locali", text: "Atelier d'arte contemporanea, cortili nascosti e degustazioni nei bistrot" }
    ]
  },
  {
    id: "tokyo",
    title: "Tokyo",
    tagline: "Tradizione millenaria & neon del futuro",
    style: "Avventura Urbana",
    badge: "Avventura",
    // Foto vivace e nitida con vista aperta sulla metropoli
    image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=1200&q=85",
    highlights: [
      { title: "Metropoli & Luci", text: "I vicoli senza tempo di Omoide Yokocho e le vertigini futuristiche di Shinjuku" },
      { title: "Tradizione & Futuro", text: "L'alba silenziosa al tempio Senso-ji e le installazioni digitali di TeamLab" },
      { title: "Design & Lifestyle", text: "Quartieri del design tra Omotesando e Ginza con caffè specialty d'autore" }
    ]
  },
  {
    id: "ibiza",
    title: "Ibiza",
    tagline: "Sunset sessions, beach club & notti esclusive",
    style: "Nightlife & Vibe",
    badge: "Nightlife",
    // Foto spettacolare con caletta caraibica, mare turchese acceso e sole
    image: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=1200&q=85",
    highlights: [
      { title: "Sunset Sessions", text: "Aperitivi leggendari al Café del Mar e vista magnetica sui faraglioni di Es Vedrà" },
      { title: "Beach Club & Mare", text: "Pomeriggi pieds dans l'eau a Cala Jondal e traversate verso le acque di Formentera" },
      { title: "Clubbing Mondiale", text: "Gli eventi open-air all'Ushuaïa e i dj-set d'avanguardia all'Hï Ibiza e Pacha" }
    ]
  },
  {
    id: "roma",
    title: "Roma",
    tagline: "Scorci barocchi, vicoli & gusto autentico",
    style: "Classico & Cucina",
    badge: "Classico",
    // Colosseo con luce calda e cielo azzurro splendente
    image: "https://images.unsplash.com/photo-1529260830199-42c24126f198?auto=format&fit=crop&w=1200&q=85",
    highlights: [
      { title: "Piazze & Fontana", text: "Scorci barocchi tra Pantheon e Piazza Navona con soste nelle botteghe storiche" },
      { title: "Tramonti Capitolini", text: "Passeggiate senza fretta tra i Fori Imperiali e la vista mozzafiato dal Pincio" },
      { title: "Trastevere Autentica", text: "Serate veraci tra le trattorie storiche e i caffè d'epoca del centro" }
    ]
  }
];

export default function PreviewHome() {
  const [selectedDest, setSelectedDest] = useState(curatedDestinations[0]);

  return (
    <div className="min-h-[calc(100vh-80px)] text-[#EDE7D8] relative overflow-hidden font-body flex flex-col justify-center">
      
      {/* Sfumature e bagliori d'atmosfera */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-[#D4AF37]/10 blur-[130px] pointer-events-none -z-10" />
      <div className="absolute bottom-10 right-1/4 w-[450px] h-[350px] bg-[#1E1B4B]/40 blur-[120px] pointer-events-none -z-10" />

      <main className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-8 flex flex-col gap-8">
        
        {/* HERO */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#18143E]/90 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-semibold uppercase tracking-wider mb-4 shadow-lg shadow-[#0B091F]/50">
            <Compass className="w-4 h-4 text-[#D4AF37] animate-pulse" />
            AI Travel Atelier
          </div>

          <h1 className="text-3xl sm:text-5xl md:text-6xl font-serif text-white leading-[1.15] mb-3 font-normal tracking-tight">
            Il tuo viaggio, <span className="italic text-[#D4AF37]">cucito su misura da un Genio.</span>
          </h1>

          <p className="text-sm sm:text-base text-[#EDE7D8]/80 font-light max-w-xl mx-auto mb-6">
            Nessun itinerario preconfezionato. Orari reali, tappe iconiche e il ritmo ideale calibrato esclusivamente per te.
          </p>

          <div className="flex items-center justify-center gap-3">
            <Link
              to="/plan"
              className="px-7 py-3 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#B79A5A] hover:brightness-110 text-[#0F0C20] font-bold text-sm flex items-center gap-2 transition-all shadow-xl shadow-[#D4AF37]/10 active:scale-95"
            >
              Inizia a Pianificare
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* SELEZIONE METE */}
        <div className="w-full flex flex-col gap-4">
          <div className="flex items-center justify-between px-1">
            <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Lasciati Ispirare dalle mete del momento:
            </span>
            <span className="text-xs text-[#EDE7D8]/60 hidden sm:inline">
              Seleziona una città per esplorarne il mood
            </span>
          </div>

          {/* 4 Card delle Mete: Foto Solari, Chiare e Senza Filtri Scuri */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
            {curatedDestinations.map((dest) => {
              const isSelected = selectedDest.id === dest.id;
              return (
                <button
                  key={dest.id}
                  onClick={() => setSelectedDest(dest)}
                  className={`relative text-left rounded-xl overflow-hidden p-4 transition-all duration-300 border flex flex-col justify-end h-44 sm:h-52 group shadow-xl ${
                    isSelected
                      ? "border-[#D4AF37] ring-4 ring-[#D4AF37]/40 translate-y-[-3px] shadow-2xl shadow-[#D4AF37]/30"
                      : "border-white/10 hover:border-[#D4AF37]/60 opacity-95 hover:opacity-100"
                  }`}
                >
                  <div className="absolute inset-0 -z-10 overflow-hidden">
                    <img 
                      src={dest.image} 
                      alt={dest.title} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 brightness-100 contrast-105"
                    />
                    {/* Solo una sfumatura minima in basso per far risaltare il testo senza scurire la foto */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B091F]/90 via-[#0B091F]/20 to-transparent" />
                  </div>

                  <span className="inline-block px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[#0F0C20]/85 text-[#D4AF37] border border-[#D4AF37]/50 mb-2 w-fit backdrop-blur-md shadow">
                    {dest.badge}
                  </span>

                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
                    {dest.title}
                  </h3>
                  <p className="text-xs text-white/90 line-clamp-1 mt-0.5 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)] font-medium">
                    {dest.tagline}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Dettaglio Atmosfera */}
          <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#18143E]/85 backdrop-blur-md p-5 sm:p-6 shadow-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-[#D4AF37]/20 mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg sm:text-xl font-serif text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#D4AF37]" />
                    Atmosfere ed Esperienze a <span className="text-[#D4AF37] font-semibold">{selectedDest.title}</span>
                  </h2>
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#0F0C20] text-[#D4AF37] border border-[#D4AF37]/30">
                    {selectedDest.style}
                  </span>
                </div>
                <p className="text-xs text-[#EDE7D8]/60 mt-1">
                  Definisci tu la durata del soggiorno: il Genio calibrerà le tappe su misura per il tuo tempo.
                </p>
              </div>

              <Link
                to={`/plan?destination=${encodeURIComponent(selectedDest.title)}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/25 text-[#D4AF37] font-semibold text-xs transition-all active:scale-95 shrink-0"
              >
                Pianifica per {selectedDest.title}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* 3 Pilastri di Esperienza */}
            <div className="grid sm:grid-cols-3 gap-3 text-xs">
              {selectedDest.highlights.map((item, idx) => (
                <div 
                  key={idx} 
                  className="bg-[#0F0C20]/70 border border-[#D4AF37]/20 hover:border-[#D4AF37]/45 rounded-xl p-4 flex flex-col justify-between transition-colors"
                >
                  <span className="text-xs font-bold text-[#D4AF37] font-serif mb-1.5">
                    {item.title}
                  </span>
                  <p className="text-xs sm:text-sm text-[#EDE7D8]/80 leading-relaxed font-light">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-[#D4AF37]/20 flex flex-wrap items-center justify-between gap-3 text-xs text-[#EDE7D8]/60">
              <div className="flex items-center gap-5">
                <span className="flex items-center gap-1.5"><Utensils className="w-4 h-4 text-[#D4AF37]" /> Selezione locale accurata</span>
                <span className="flex items-center gap-1.5"><Camera className="w-4 h-4 text-[#D4AF37]" /> Orari ottimali e zero code</span>
              </div>
              <span className="text-[#EDE7D8]/50 font-light">Completamente personalizzabile</span>
            </div>
          </div>

        </div>

      </main>
    </div>
  );
}
